import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getCurrentDate } from "../../../configs/helpers";
import {
    fetchedAllSellers,
    fetchedPaymentsByDateBySeller,
} from "../../../apis/apis.accounting/apisCash";
import ReactHTMLTableToExcel from "react-html-table-to-excel";

// ── shared styles ────────────────────────────────────────────────────────────
const inputCls =
    "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls =
    "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";
const thCls =
    "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600";
const tdCls =
    "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";
const money = (v) => `S/ ${Number(v).toFixed(2)}`;

// Payment type badge
const TYPE_BADGE = {
    E: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    DY: "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300",
    DC: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
};

export function DailyCollectionPage() {
    const [sellers, setSellers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState({
        totalsByMethod: [],
        grandTotal: 0,
        totalOperations: 0,
        creditSummary: {},
    });
    const [isLoading, setIsLoading] = useState(false);
    const [sellerName, setSellerName] = useState("");

    const [filters, setFilters] = useState({
        userID: "0",
        searchDate: getCurrentDate("-"),
        selectedGangs: [],
    });

    const getAllSellers = async () => {
        try {
            const res = await fetchedAllSellers();
            setSellers(res.filter((s) => s.isActive));
        } catch (e) {
            throw e;
        }
    };

    useEffect(() => {
        getAllSellers();
    }, []);

    const handleChange = (e) =>
        setFilters({ ...filters, [e.target.name]: e.target.value });

    const handleSearch = async () => {
        if (filters.userID === "0") {
            toast.warning("Seleccione un vendedor");
            return;
        }
        setIsLoading(true);
        try {
            const res = await fetchedPaymentsByDateBySeller({
                userID: Number(filters.userID),
                searchDate: filters.searchDate,
                selectedGangs: filters.selectedGangs,
            });
            console.log("Raw payments:", res);

            // Process payments to ensure operationTotalPurchased is present!
            const paymentMap = {};
            // First pass to initialize groups
            (res.payments || []).forEach((p) => {
                if (!paymentMap[p.operationID]) {
                    paymentMap[p.operationID] = {
                        totalPurchased: p.totalPurchased,
                        payments: [],
                        runningTotal: 0,
                    };
                }
                paymentMap[p.operationID].payments.push(p);
            });
            const processedPayments = (res.payments || []).map((p) => {
                const group = paymentMap[p.operationID];
                // If API already has pendingAfterThisPayment, use it; otherwise calculate!
                let pendingAfter = p.pendingAfterThisPayment;
                if (pendingAfter === undefined) {
                    group.runningTotal += p.total;
                    pendingAfter = Math.max(
                        0,
                        group.totalPurchased - group.runningTotal,
                    );
                }
                return {
                    ...p,
                    operationTotalPurchased: p.totalPurchased, // Always add this field!
                    pendingAfterThisPayment: pendingAfter,
                };
            });

            console.log("Processed payments:", processedPayments);
            setPayments(processedPayments);

            setSummary({
                totalsByMethod: res.totalsByMethod ?? [],
                grandTotal: res.grandTotal ?? 0,
                totalOperations: res.totalOperations ?? 0,
                creditSummary: res.creditSummary ?? {},
            });

            // Capture seller name from first record
            if (res.payments?.length > 0)
                setSellerName(
                    res.payments[0].sellerName || res.payments[0].userName,
                );
            else setSellerName("");

            if (!res.payments?.length)
                toast.warning(
                    "No hay registros para los filtros seleccionados.",
                );
        } catch (e) {
            toast.error("Error al obtener los datos");
            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Cobranza diaria por vendedor
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">
                                    Finanzas
                                </li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">
                                    /
                                </li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">
                                    Cobranza diaria
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4 space-y-4">
                    {/* Filter panel */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-filter text-blue-500"></i>
                                Filtros de búsqueda
                            </h2>
                        </div>
                        <div className="px-5 py-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
                                {/* Fecha */}
                                <div className="lg:col-span-2">
                                    <label
                                        htmlFor="searchDate"
                                        className={labelCls}
                                    >
                                        <i className="fas fa-calendar mr-1"></i>
                                        Fecha
                                    </label>
                                    <input
                                        type="date"
                                        id="searchDate"
                                        name="searchDate"
                                        value={filters.searchDate}
                                        onChange={handleChange}
                                        className={inputCls}
                                    />
                                </div>

                                {/* Vendedor */}
                                <div className="lg:col-span-4">
                                    <label
                                        htmlFor="userID"
                                        className={labelCls}
                                    >
                                        <i className="fas fa-user mr-1"></i>
                                        Vendedor
                                    </label>
                                    <select
                                        id="userID"
                                        name="userID"
                                        value={filters.userID}
                                        onChange={handleChange}
                                        className={inputCls}
                                    >
                                        <option value="0" disabled>
                                            Elegir...
                                        </option>
                                        {sellers.map((s, i) => (
                                            <option key={i} value={s.sellerID}>
                                                {s.sellerName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Buscar */}
                                <div className="lg:col-span-2">
                                    <button
                                        onClick={handleSearch}
                                        disabled={isLoading}
                                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                                    >
                                        {isLoading ? (
                                            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                        ) : (
                                            <i className="fas fa-search"></i>
                                        )}
                                        {isLoading ? "Buscando..." : "Buscar"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary cards — only when there's data */}
                    {payments.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                            {/* Seller info */}
                            <div className="rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-5 py-4 flex items-center gap-3">
                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                                    <i className="fas fa-user text-blue-600 dark:text-blue-300"></i>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs text-blue-500 dark:text-blue-400 font-medium uppercase tracking-wide">
                                        Vendedor
                                    </p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                        {sellerName}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {filters.searchDate}
                                    </p>
                                </div>
                            </div>

                            {/* Grand total */}
                            <div className="rounded-xl border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-5 py-4 text-center">
                                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium uppercase tracking-wide mb-1">
                                    Total cobrado
                                </p>
                                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                                    {money(summary.grandTotal)}
                                </p>
                            </div>

                            {/* Total operations */}
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4 text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1">
                                    Operaciones
                                </p>
                                <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                                    {summary.totalOperations}
                                </p>
                            </div>

                            {/* Total pending */}
                            <div className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-5 py-4 text-center">
                                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium uppercase tracking-wide mb-1">
                                    Total pendiente
                                </p>
                                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                                    {money(
                                        payments.reduce(
                                            (s, p) =>
                                                s + p.pendingAfterThisPayment,
                                            0,
                                        ),
                                    )}
                                </p>
                            </div>

                            {/* By method */}
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4">
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-2">
                                    Por método
                                </p>
                                <div className="space-y-1.5">
                                    {summary.totalsByMethod.map((m) => (
                                        <div
                                            key={m.paymentType}
                                            className="flex items-center justify-between"
                                        >
                                            <span
                                                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_BADGE[m.paymentType] ?? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"}`}
                                            >
                                                {m.paymentTypeDisplay}
                                            </span>
                                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                                {m.count} · {money(m.total)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Results table */}
                    {payments.length > 0 ? (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                            {/* Card header */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div>
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        <i className="fas fa-money-bill-wave text-emerald-500"></i>
                                        Detalle de cobros
                                    </h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {payments.length} registros
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {/* Legend */}
                                    <div className="hidden md:flex items-center gap-2 text-xs">
                                        {summary.totalsByMethod.map((m) => (
                                            <span
                                                key={m.paymentType}
                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${TYPE_BADGE[m.paymentType] ?? "bg-gray-100 text-gray-600"}`}
                                            >
                                                {m.paymentTypeDisplay}
                                            </span>
                                        ))}
                                    </div>
                                    <ReactHTMLTableToExcel
                                        id="daily-collection-xls"
                                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 transition-colors"
                                        table="table-daily-collection"
                                        filename={`cobranza_diaria_${filters.searchDate}`}
                                        sheet="cobranza"
                                        buttonText="↓ Exportar XLS"
                                    />
                                </div>
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto bg-white dark:bg-gray-900">
                                <table
                                    className="min-w-full text-sm border-separate border-spacing-0"
                                    id="table-daily-collection"
                                >
                                    <thead className="sticky top-0 z-10">
                                        <tr>
                                            <th
                                                className={`${thCls} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}
                                            >
                                                #
                                            </th>
                                            <th
                                                className={`${thCls} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}
                                            >
                                                Fecha pago
                                            </th>
                                            <th
                                                className={`${thCls} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}
                                            >
                                                Método
                                            </th>
                                            <th
                                                className={`${thCls} bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300`}
                                            >
                                                Documento
                                            </th>
                                            <th
                                                className={`${thCls} bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 text-left`}
                                            >
                                                Cliente
                                            </th>
                                            <th
                                                className={`${thCls} bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300`}
                                            >
                                                Ruta
                                            </th>
                                            <th
                                                className={`${thCls} bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300`}
                                            >
                                                Fecha venta
                                            </th>
                                            <th
                                                className={`${thCls} bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300`}
                                            >
                                                Cobrador
                                            </th>
                                            <th
                                                className={`${thCls} bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300`}
                                            >
                                                Vendedor
                                            </th>
                                            <th
                                                className={`${thCls} bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-l-2 border-blue-200 dark:border-blue-700`}
                                            >
                                                Comprado
                                            </th>
                                            <th
                                                className={`${thCls} bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300`}
                                            >
                                                Pendiente
                                            </th>
                                            <th
                                                className={`${thCls} bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300`}
                                            >
                                                Al crédito
                                            </th>
                                            <th
                                                className={`${thCls} bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300`}
                                            >
                                                Yape
                                            </th>
                                            <th
                                                className={`${thCls} bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300`}
                                            >
                                                Devuelto
                                            </th>
                                            <th
                                                className={`${thCls} bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300`}
                                            >
                                                Efectivo
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((item, i) => {
                                            const hasPending =
                                                Number(
                                                    item.pendingAfterThisPayment,
                                                ) > 0;
                                            return (
                                                <tr
                                                    key={item.cashFlowID}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                                >
                                                    <td
                                                        className={`${tdCls} text-center text-gray-500 dark:text-gray-400`}
                                                    >
                                                        {i + 1}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}
                                                    >
                                                        {item.transactionDate}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center`}
                                                    >
                                                        <span
                                                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_BADGE[item.paymentType] ?? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"}`}
                                                        >
                                                            {
                                                                item.paymentTypeDisplay
                                                            }
                                                        </span>
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center font-medium text-sky-700 dark:text-sky-300 bg-sky-50/50 dark:bg-sky-900/10`}
                                                    >
                                                        {
                                                            item.operationDocumentNumber
                                                        }
                                                    </td>
                                                    <td
                                                        className={`${tdCls} font-medium text-gray-800 dark:text-gray-200 bg-sky-50/50 dark:bg-sky-900/10`}
                                                    >
                                                        {item.clientName}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center text-gray-600 dark:text-gray-400 bg-sky-50/50 dark:bg-sky-900/10`}
                                                    >
                                                        {item.clientGangName}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center text-gray-600 dark:text-gray-400 bg-sky-50/50 dark:bg-sky-900/10`}
                                                    >
                                                        {item.operationDate}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center text-gray-700 dark:text-gray-300 bg-pink-50/50 dark:bg-pink-900/10`}
                                                    >
                                                        {item.userName}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-center text-gray-700 dark:text-gray-300 bg-violet-50/50 dark:bg-violet-900/10`}
                                                    >
                                                        {item.sellerName}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-right font-semibold text-blue-700 dark:text-blue-300 bg-blue-50/50 dark:bg-blue-900/10 border-l-2 border-blue-100 dark:border-blue-800`}
                                                    >
                                                        {money(
                                                            item.totalPurchased,
                                                        )}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-right font-semibold ${hasPending ? "text-red-600 dark:text-red-400 bg-red-50/50 dark:bg-red-900/10" : "text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10"}`}
                                                    >
                                                        {money(
                                                            item.pendingAfterThisPayment,
                                                        )}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-right font-semibold text-cyan-700 dark:text-cyan-300 bg-cyan-50/50 dark:bg-cyan-900/10`}
                                                    >
                                                        {money(0)}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-right font-semibold text-orange-700 dark:text-orange-300 bg-orange-50/50 dark:bg-orange-900/10`}
                                                    >
                                                        {money(
                                                            item.paymentType ===
                                                                "DY"
                                                                ? item.total
                                                                : 0,
                                                        )}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-right text-purple-700 dark:text-purple-400 bg-purple-50/50 dark:bg-purple-900/10`}
                                                    >
                                                        {money(
                                                            item.totalReturned,
                                                        )}
                                                    </td>
                                                    <td
                                                        className={`${tdCls} text-right font-semibold text-green-700 dark:text-green-300 bg-green-50/50 dark:bg-green-900/10`}
                                                    >
                                                        {money(
                                                            item.paymentType ===
                                                                "E"
                                                                ? item.total
                                                                : 0,
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-800 dark:bg-gray-950">
                                            <td
                                                colSpan={9}
                                                className="px-3 py-2 text-xs font-bold text-right text-white uppercase tracking-wide border-t border-gray-600"
                                            >
                                                <i className="fas fa-calculator mr-2"></i>
                                                TOTAL
                                            </td>
                                            <td className="px-3 py-2 text-xs text-right font-bold text-blue-300 border-t border-gray-600 border-l-2 border-blue-600">
                                                {/* For Comprado, we need to calculate unique operation totals to avoid double counting */}
                                                {money(
                                                    Array.from(
                                                        new Set(
                                                            payments.map(
                                                                (p) =>
                                                                    p.operationID,
                                                            ),
                                                        ),
                                                    ).reduce((sum, opId) => {
                                                        const item =
                                                            payments.find(
                                                                (p) =>
                                                                    p.operationID ===
                                                                    opId,
                                                            );
                                                        return (
                                                            sum +
                                                            (item
                                                                ? item.operationTotalPurchased
                                                                : 0)
                                                        );
                                                    }, 0),
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-right font-bold text-amber-300 border-t border-gray-600">
                                                {/* Pendiente footer probably not necessary or just 0 */}
                                                {money(
                                                    payments.reduce(
                                                        (s, p) =>
                                                            s +
                                                            p.pendingAfterThisPayment,
                                                        0,
                                                    ),
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-right font-bold text-cyan-300 border-t border-gray-600">
                                                {money(
                                                    payments.reduce(
                                                        (s, p) =>
                                                            s + p.totalInCredit,
                                                        0,
                                                    ),
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-right font-bold text-orange-300 border-t border-gray-600">
                                                {money(
                                                    payments.reduce(
                                                        (s, p) =>
                                                            p.paymentType ===
                                                            "DY"
                                                                ? s + p.total
                                                                : s,
                                                        0,
                                                    ),
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-right font-bold text-purple-300 border-t border-gray-600">
                                                {money(
                                                    payments.reduce(
                                                        (s, p) =>
                                                            s + p.totalReturned,
                                                        0,
                                                    ),
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-right font-bold text-green-300 border-t border-gray-600">
                                                {money(
                                                    payments.reduce(
                                                        (s, p) =>
                                                            p.paymentType ===
                                                            "E"
                                                                ? s + p.total
                                                                : s,
                                                        0,
                                                    ),
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ) : (
                        !isLoading && (
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                                <div className="py-16 text-center">
                                    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                                        <i className="fas fa-hand-holding-usd text-gray-400 dark:text-gray-500 text-2xl"></i>
                                    </div>
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                        Sin datos
                                    </p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                        Seleccione un vendedor y una fecha para
                                        ver la cobranza diaria.
                                    </p>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </section>
        </>
    );
}
